import { z } from 'zod';
import {
  type UseQueryResult,
  type QueryOptions,
  useQuery,
} from '@tanstack/react-query';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';
import { type ApiError } from '../../../../utils/api/ApiError/ApiError';
import { sourceSystemShape } from '../../v2';

export const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  country: z.string().optional(),
  source_system: sourceSystemShape,
  source_id: z.string(),
  source_id_type: z.union([z.literal('UCN'), z.literal('SourceId')]),
  address_line_1: z.string().optional(),
  address_line_2: z.string().optional(),
  parent_organization_id: z.string().optional(),
  organization_metadata: z.array(
    z.object({
      attribute_key: z.string(),
      attribute_value: z.string(),
    })
  ),
});
export type Organization = z.infer<typeof organizationSchema>;

export async function getOrganizationByIdApi(orgId?: string) {
  const org = await fetcher(`/organizations/${orgId}`, {
    service: '/roster',
    version: '/v3',
  });
  return organizationSchema.parse(org);
}

export const getOrganizationByIdApiQueryKey = (orgId?: string) =>
  ['organization', orgId].filter(Boolean);

export function useGetOrganizationByIdApi(
  { orgId }: { orgId?: string },
  customQueryOptions?: QueryOptions<Organization, ApiError>
): UseQueryResult<Organization, ApiError> {
  return useQuery({
    queryKey: getOrganizationByIdApiQueryKey(orgId),
    queryFn: () => getOrganizationByIdApi(orgId),
    enabled: Boolean(orgId),
    ...customQueryOptions,
  });
}
